/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { Box, Popover, IconButton, TextField, InputAdornment } from "@mui/material";
import ColorizeIcon from "@mui/icons-material/Colorize";
import ColorPicker from "react-pick-color";

export interface ColorPickerProps {
  id?: string;
  value?: string;
  disabled?: boolean;
  mode?: "Picker" | "Palette";
  modeSwitcher?: boolean;
  showButtons?: boolean;
  inline?: boolean;
  cssClass?: string;
  className?: string;
  enableOpacity?: boolean;
  // Callbacks - Syncfusion compatible
  change?: (args: { value: string; previousValue?: string }) => void;
  onChange?: (args: { value: string; previousValue?: string }) => void;
  created?: () => void;
  select?: (args: { currentValue: { hex: string; rgba: any } }) => void;
  beforeClose?: () => void;
  open?: () => void;
  // Style props
  style?: React.CSSProperties;
  width?: string;
}

export interface ColorPickerRef {
  value: string;
  getValue: () => string;
  setValue: (color: string) => void;
  toggle: () => void;
}

const ColorPickerComponent = forwardRef<ColorPickerRef, ColorPickerProps>((props, ref) => {
  const {
    id,
    value: propValue = "#000000",
    disabled = false,
    inline = false,
    cssClass,
    className,
    enableOpacity = false,
    change,
    onChange,
    created,
    select,
    beforeClose,
    open: openCallback,
    style,
    width = "40px",
  } = props;

  const [color, setColor] = useState(propValue);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const previousValueRef = useRef(propValue);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isOpen = Boolean(anchorEl);

  useEffect(() => {
    if (created) {
      created();
    }
  }, []);

  useEffect(() => {
    if (propValue !== undefined && propValue !== color) {
      setColor(propValue);
      previousValueRef.current = propValue;
    }
  }, [propValue]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    if (openCallback) openCallback();
  };

  const handleClose = () => {
    if (beforeClose) beforeClose();
    setAnchorEl(null);

    // Emit change event on close
    if (color !== previousValueRef.current) {
      const args = { value: color, previousValue: previousValueRef.current };
      if (change) change(args);
      if (onChange) onChange(args);
      previousValueRef.current = color;
    }
  };

  useImperativeHandle(ref, () => ({
    get value() {
      return color;
    },
    getValue: () => color,
    setValue: (newColor: string) => {
      setColor(newColor);
    },
    // Compatibility shim for the legacy Syncfusion API (CUBE-23836). When the
    // anchor button is mounted, toggle the picker open/close. When the
    // component is in `inline` mode there is no anchor — fall back to a no-op
    // so callers like the right-click context menu can never throw "toggle is
    // not a function" again.
    toggle: () => {
      if (inline) return;
      const btn = buttonRef.current;
      if (!btn) return;
      if (anchorEl) {
        handleClose();
      } else {
        setAnchorEl(btn);
        if (openCallback) openCallback();
      }
    },
  }));

  const handleColorChange = (newColor: { hex: string; rgb: { r: number; g: number; b: number; a: number } }) => {
    const colorValue = enableOpacity
      ? `rgba(${newColor.rgb.r}, ${newColor.rgb.g}, ${newColor.rgb.b}, ${newColor.rgb.a})`
      : newColor.hex;
    setColor(colorValue);

    if (select) {
      select({
        currentValue: {
          hex: newColor.hex,
          rgba: newColor.rgb,
        },
      });
    }
  };

  if (inline) {
    return (
      <Box id={id} className={className || cssClass} style={style}>
        <ColorPicker color={color} onChange={handleColorChange} hideAlpha={!enableOpacity} />
      </Box>
    );
  }

  return (
    <Box id={id} className={className || cssClass} style={{ display: "inline-flex", ...style }}>
      <IconButton
        ref={buttonRef}
        onClick={handleClick}
        disabled={disabled}
        sx={{
          width,
          height: width,
          padding: "0.25rem",
          border: "1px solid #ccc",
          borderRadius: "0.25rem",
          backgroundColor: color,
          "&:hover": {
            backgroundColor: color,
            opacity: 0.9,
          },
          "&.Mui-disabled": {
            backgroundColor: color,
            opacity: 0.5,
          },
        }}
      >
        <ColorizeIcon
          sx={{
            fontSize: "1rem",
            color: isLightColor(color) ? "#333" : "#fff",
          }}
        />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              padding: "0.5rem",
              borderRadius: "0.5rem",
            },
          },
        }}
      >
        <ColorPicker color={color} onChange={handleColorChange} hideAlpha={!enableOpacity} />
        <TextField
          value={color}
          onChange={(e) => setColor(e.target.value)}
          size="small"
          sx={{
            marginTop: "0.5rem",
            width: "100%",
            "& .MuiInputBase-input": {
              padding: "0.375rem 0.5rem",
              fontSize: "0.75rem",
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    sx={{
                      width: "1.25rem",
                      height: "1.25rem",
                      backgroundColor: color,
                      borderRadius: "0.25rem",
                      border: "1px solid #ccc",
                    }}
                  />
                </InputAdornment>
              ),
            },
          }}
        />
      </Popover>
    </Box>
  );
});

// Helper function to determine if a color is light
function isLightColor(color: string): boolean {
  let r, g, b;

  if (color.startsWith("#")) {
    const hex = color.slice(1);
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else if (color.startsWith("rgb")) {
    const match = color.match(/\d+/g);
    if (match) {
      [r, g, b] = match.map(Number);
    } else {
      return false;
    }
  } else {
    return false;
  }

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

ColorPickerComponent.displayName = "ColorPickerComponent";

export default ColorPickerComponent;
